import OrganizationName from "@/components/onboarding/organization-name";
import { Spinner } from "@/components/ui/spinner";
import OrganizationWebsite from "@/components/onboarding/organization-website";
import AffiliatePageLink from "@/components/onboarding/affiliate-page-linlk";
import ReviewForm from "@/components/onboarding/review-form";
import { Button } from "@/components/ui/button";
import {
  Stepper,
  StepperDescription,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
} from "@/components/ui/stepper";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  LogOutIcon,
  Megaphone,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import {
  OrganizationSwitcher,
  SignOutButton,
  useAuth,
  useOrganization,
  useOrganizationList,
  UserButton,
  useUser,
} from "@clerk/react";
import { useOrganizationSettingsStore } from "@/stores";
import { toast } from "sonner";
import SearchCreators from "@/components/onboarding/search-creators";
import { ScrollArea } from "@/components/ui/scroll-area";
import CreateFaq from "@/components/onboarding/create-faq-brief";
import PlanSelector from "@/components/onboarding/plans/PlanSelector";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import HelpDialog from "@/components/ui/VideoHelper";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const steps = [
  {
    step: 1,
    title: "Organization Name",
    description: "Name your team.",
  },
  {
    step: 2,
    title: "Organization Website",
    description: "We'll use that later",
  },
  {
    step: 3,
    title: "Afiliate Page Link",
    description: "Info about your affiliate program",
  },
  {
    step: 4,
    title: "Get Creators",
    description: "See how Amy searches for creators",
  },
  {
    step: 5,
    title: "Generate FAQ",
    description: "Your Brands FAQ",
  },
  {
    step: 6,
    title: "Review & Submit",
    description: "Review your information",
  },
  {
    step: 7,
    title: "Choose your plan",
    description: "Choose a plan that suits your needs",
  },
];

export interface OnboardingData {
  organization_name: string;
  website_url: string;
  affiliate_page_link: string;
  search_creators_query: string;
  faq_content: string;
}

export default function Onboarding() {
  const { userId, getToken } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const {
    isLoaded: membershipLoaded,
    userMemberships,
    setActive,
  } = useOrganizationList({
    userMemberships: { infinite: true },
  });

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<OnboardingData>({
    organization_name: "",
    website_url: "",
    affiliate_page_link: "",
    search_creators_query: "",
    faq_content: "",
  });

  const { organizationInfo, isLoading, fetchOrganizationInfo } =
    useOrganizationSettingsStore();

  const hasNoExternalAccounts = user?.externalAccounts?.length === 0;
  const externalAccaountHasError =
    user?.externalAccounts[0]?.verification?.error;
  // The Google-connect step is opt-in: only users flagged in Clerk with
  // publicMetadata.require_oauth === true (legacy-platform senders) see it.
  const requireOauth = user?.publicMetadata?.require_oauth === true;

  const updateFormData = (data: Partial<OnboardingData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    if (step < steps.length) {
      setStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    }
  };

  const handleReviewSubmit = async () => {
    setIsSubmitting(true);
    try {
      const token = await getToken();

      const response = await fetch(
        `${API_BASE_URL}/api/auth/completeOnboarding`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_id: userId,
            ...formData,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to complete onboarding");
      }

      const data = await response.json();
      if (data.success) {
        if (data.organization_id && setActive) {
          await setActive({ organization: data.organization_id });
        }
        toast.success("Information saved successfully!");
        handleNext(); // Move to step 5 (plan selection)
      }
    } catch (error) {
      console.error("Error during onboarding:", error);
      toast.error("Failed to save information. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.organization_name;
      case 2:
        return formData.website_url;
      case 3:
        return formData.website_url;
      case 4:
        return formData.search_creators_query;
      case 5:
        return true;
      case 6:
        return true;
      default:
        return false;
    }
  };

  useEffect(() => {
    // set the website_url and affiliate_page_link if they exist in organizationInfo
    if (organizationInfo?.website_url) {
      setFormData((prev) => ({
        ...prev,
        website_url: organizationInfo.website_url,
      }));
    }
    if (organizationInfo?.affiliate_page_link) {
      setFormData((prev) => ({
        ...prev,
        affiliate_page_link: organizationInfo.affiliate_page_link,
      }));
    }
    if (organizationInfo?.faq) {
      setFormData((prev) => ({ ...prev, faq_content: organizationInfo.faq }));
    }
    if (organization?.name) {
      setFormData((prev) => ({
        ...prev,
        organization_name: organization?.name,
      }));
    }
  }, [isLoading]);

  useEffect(() => {
    // check what step to start with based on user data

    if (requireOauth && (hasNoExternalAccounts || externalAccaountHasError)) {
      console.log(
        "No external accounts found, redirecting to connect Google step."
      );
      setStep(0);
    } else if (
      user?.externalAccounts[0]?.verification?.status != "unverified" &&
      // user?.externalAccounts.length > 0 &&
      step === 0
    ) {
      console.log("External accounts found, skipping connect Google step.");
      setStep(1);
    }

    if (user?.organizationMemberships?.length != 0) {
      console.log(
        "Organization memberships found, Skipping organization step."
      );
      setStep(4);
    }

    if (organizationInfo?.faq) {
      console.log("FAQ found, skipping FAQ step.");
      setStep(5);
    }
  }, [organizationInfo, isLoading]);

  useEffect(() => {
    const fetchOrgInfo = async () => {
      const token = await getToken();
      if (organization?.id && user?.id && token) {
        fetchOrganizationInfo(organization.id, user?.id, token);
      }
    };
    fetchOrgInfo();
  }, [organization?.id, userId, getToken, fetchOrganizationInfo]);

  if (isLoading) {
    return (
      <div className="w-full h-dvh flex items-center justify-center bg-linear-to-br from-foreground/10 to-accent p-4">
        <Spinner className="w-6 h-6" />
      </div>
    );
  }

  return (
    <main className="min-h-screen flex items-start justify-center dark:bg-muted overflow-clip">
      {step != 7 && (
        <div className="w-1/3 min-w-80 flex flex-col justify-between items-start border-r dark:bg-sidebar bg-accent-foreground/5 p-5 gap-5 min-h-screen">
          <div className="flex flex-col items-center justify-between gap-5 w-full">
            <div className="flex flex-row items-center justify-between w-full">
              <img
                src="/logo_full_primary.svg"
                className="h-10 w-auto object-contain"
              />
              {/* <img src="/logo_full_black.svg" className="h-10 w-auto object-contain block dark:hidden" />
            <img src="/logo_full_white.svg" className="h-10 w-auto object-contain hidden dark:block" /> */}
              <div className="flex flex-row items-center gap-2">
                <HelpDialog
                  featureId="onboarding"
                  title="Need help?"
                  description="Watch this video to learn how to complete the onboarding process."
                  videoUrl="https://www.youtube.com/embed/aEq1aWp80bg?si=K81bKjaE0V2fc0E_"
                  triggerText=""
                  variant="ghost"
                  size="icon"
                />
                <SignOutButton>
                  <Button
                    variant="muted-ghost" size="icon-md"
                   
                  >
                    <LogOutIcon />
                  </Button>
                </SignOutButton>
              </div>
            </div>
            {/* only show org switcher when the user already belongs to ≥1 org */}
            {membershipLoaded && userMemberships?.count > 0 && (
              <div className="flex flex-row items-center justify-between w-full">
                <OrganizationSwitcher hidePersonal />
                <UserButton />
              </div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full flex flex-col justify-between items-center"
          >
            <Stepper
              defaultValue={1}
              orientation="vertical"
              value={step}
              onValueChange={setStep}
            >
              {steps.map(({ step, title, description }) => (
                <StepperItem
                  key={step}
                  step={step}
                  className="relative items-start not-last:flex-1"
                  // loading={step == step}
                >
                  <div className="flex flex-row gap-2 items-start rounded-xs pb-16 last:pb-0">
                    <StepperTrigger
                      asChild
                      className="bg-muted-foreground/10 dark:bg-muted"
                    >
                      <StepperIndicator className="bg-muted-foreground/10 dark:bg-muted" />
                    </StepperTrigger>
                    <div className="mt-1 space-y-1 px-2 text-left">
                      <StepperTitle>{title}</StepperTitle>
                      <StepperDescription>{description}</StepperDescription>
                    </div>
                  </div>
                  {step < steps.length && (
                    <StepperSeparator className="absolute inset-y-0 top-[1.625rem] left-3 -order-1 m-0 -translate-x-1/2 group-data-[orientation=horizontal]/stepper:w-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=horizontal]/stepper:flex-none group-data-[orientation=vertical]/stepper:h-[calc(100%-1.5rem-0.25rem)]" />
                  )}
                </StepperItem>
              ))}
            </Stepper>
          </motion.div>

          <div className="w-full text-muted-foreground text-sm">
            Copyright © 2025 Uplodio
            <br />
            Scale Creator Marketing Without Scaling Headcount
          </div>
        </div>
      )}

      <ScrollArea className="w-full gap-5 h-screen flex flex-col justify-center items-center relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full gap-5 min-h-screen flex flex-col justify-center items-center relative"
        >
          <div className="absolute inset-0 bg-[repeating-linear-gradient(315deg,--theme(--color-muted-foreground/0.3)_0,--theme(--color-muted-foreground/0.5)_1px,transparent_0,transparent_50%)] bg-size-[10px_10px] bg-fixed opacity-30"></div>
          <div
            className={`flex flex-col justify-between items-start ${
              [5, 7].includes(step) ? "w-2xl" : "w-md"
            } ${[7].includes(step) ? "w-4xl" : ""}`}
          >
            {[1, 2, 3, 5, 6, 7].includes(step) && (
              <Button
                variant="ghost"
                className="shrink-0 self-left dark:hover:bg-card group"
                onClick={handleBack}
                disabled={step === 1}
              >
                <ArrowLeftIcon className="group-hover:-translate-x-0.5 group-active:-translate-x-1 transition-all" />
                Back
              </Button>
            )}
          </div>
          <div className="z-10 w-full h-full flex flex-col justify-center items-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {step === 0 &&
                  requireOauth &&
                  (hasNoExternalAccounts || externalAccaountHasError) && (
                    <ConnectGoogle />
                  )}
                {step === 1 && (
                  <OrganizationName
                    formData={formData}
                    updateFormData={updateFormData}
                    handleNext={handleNext}
                  />
                )}
                {step === 2 && (
                  <OrganizationWebsite
                    formData={formData}
                    updateFormData={updateFormData}
                    handleNext={handleNext}
                  />
                )}
                {step === 3 && (
                  <AffiliatePageLink
                    formData={formData}
                    updateFormData={updateFormData}
                    handleNext={handleNext}
                  />
                )}
                {step === 4 && (
                  <SearchCreators
                    formData={formData}
                    updateFormData={updateFormData}
                    handleNext={handleNext}
                  />
                )}
                {step === 5 && (
                  <CreateFaq
                    formData={formData}
                    updateFormData={updateFormData}
                    handleNext={handleNext}
                  />
                )}
                {step === 6 && (
                  <>
                    <ReviewForm />
                  </>
                )}
                {step === 7 && (
                  <>
                    <PlanSelector />
                  </>
                )}
              </motion.div>
            </AnimatePresence>

            {[1, 2].includes(step) && (
              <Button
                className="w-md group"
                onClick={handleNext}
                disabled={!isStepValid()}
              >
                Next step
                <ArrowRightIcon className="group-hover:translate-x-0.5 group-active:translate-x-1 transition-all" />
              </Button>
            )}
            {step === 6 && (
              <Button
                className="w-md group"
                onClick={handleReviewSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Spinner />
                ) : (
                  <Megaphone className="group-hover:-rotate-12 transition-all" />
                )}
                Scale Creator Marketing!
              </Button>
            )}
          </div>
        </motion.div>
      </ScrollArea>
    </main>
  );
}

function ConnectGoogle() {
  const { user } = useUser();

  console.log("User external accounts:", user?.externalAccounts);

  const handleConnectGoogle = async () => {
    if (!user) {
      toast.error("You must be logged in to connect a Google account.");
      return;
    }
    const response = await user.createExternalAccount({
      strategy: "oauth_google",
      redirectUrl: `${window.location.origin}${location.pathname}`,
    });

    if (response?.verification?.externalVerificationRedirectURL) {
      window.location.href =
        response.verification.externalVerificationRedirectURL.href;
    }
  };

  const hasNoExternalAccounts = user?.externalAccounts?.length === 0;
  const externalAccountExpired =
    user?.externalAccounts[0]?.verification?.status === "expired";
  const externalAccaountHasError =
    user?.externalAccounts[0]?.verification?.error;

  return (
    <div className="flex flex-col items-center gap-5 p-5 bg-background border border-dashed rounded-md w-auto max-w-md">
      <span className="flex flex-col text-sm">
        <span className="font-medium">Lets connect your Google account!</span>
        <span className="text-xs text-muted-foreground/70">
          Amy will use your gmail account to send and read emails on your
          behalf.
        </span>
      </span>
      {externalAccaountHasError && (
        <Alert variant="destructive">
          <AlertTitle>
            <AlertCircleIcon />
            {externalAccaountHasError &&
              user?.externalAccounts[0].verification?.error?.message}
            {hasNoExternalAccounts &&
              "Give Amy access to your Gmail to get started."}
            {externalAccountExpired && "Your Google account has expired."}
          </AlertTitle>
          <AlertDescription>
            {externalAccaountHasError &&
              user?.externalAccounts[0].verification?.error?.longMessage}
            {hasNoExternalAccounts &&
              "Amy needs access to your Gmail to send emails to creators from your inbox."}
            {externalAccountExpired &&
              "Since your Google account has expired, you will need to connect it again."}
          </AlertDescription>
        </Alert>
      )}
      <Button
        onClick={handleConnectGoogle}
        className="w-full"
        variant="neutral"
      >
        <img src="/google.svg" className="size-4" alt="G" />
        Connect Google
      </Button>
    </div>
  );
}
